/**
 * AICopilotModal — Floating AI Copilot FAB Modal
 * Sprint 4: Context-aware modal with mini chat
 * Sprint 5: Agentic workflows — copilot can ACT, not just suggest
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import {
  X, Send, Sparkles, Maximize2, FileText, BookOpen,
  Zap, Users, Calendar, MessageCircle, TrendingUp,
  Search, MapPin, AlertCircle, Clock, History, ChevronDown,
} from 'lucide-react';
import chatThreadService from '../services/chatThreadService';
import { motion, AnimatePresence } from 'motion/react';
import { notify } from '../utils/notifications';
import { useApp } from '../context/AppContext';
import {
  generateAIResponse,
  getContextSuggestions,
  getAgentActionDescription,
  filterActionChips,
  type AIContext,
  type ActionChip,
} from '../services/aiService';
import type { AgentActionType } from '../context/AppContext';
import { MarkdownContent } from './MarkdownContent';
import { ReportAIResponseButton } from './ReportAIResponseButton';
import { useEventModules } from '../hooks/useEventModules';
import { useEventData } from '../context/EventDataContext';
import { previewCopilotResponse } from '../preview/previewAI';
import { useKeyboardVisible } from '../hooks/useKeyboardVisible';
import { AIAvailabilityNotice } from './AIAvailabilityNotice';

// ─── Icon resolver ─────────────────────────────────────────────────────────

const ICON_MAP: Record<string, React.FC<{ className?: string }>> = {
  Calendar, Zap, Users, FileText, Search, Sparkles,
  BookOpen, MessageCircle, TrendingUp, MapPin, AlertCircle, Clock,
};

function SuggestionIcon({ name, className }: { name: string; className?: string }) {
  const Icon = ICON_MAP[name] || Sparkles;
  return <Icon className={className || 'w-3.5 h-3.5'} />;
}

// ─── Screen detection ─────────────────────────────────────────────────────────

function detectScreen(pathname: string): string {
  if (pathname.startsWith('/home')) return 'home';
  if (pathname.startsWith('/agenda')) return 'agenda';
  if (pathname.startsWith('/library')) return 'library';
  if (pathname.startsWith('/explore')) return 'explore';
  if (pathname.match(/^\/session\/[^/]+$/)) return 'session';
  if (pathname.match(/^\/session\/[^/]+\/prep$/)) return 'session';
  if (pathname.startsWith('/paper/')) return 'paper';
  if (pathname.startsWith('/speaker/')) return 'speaker';
  if (pathname.startsWith('/attendee/')) return 'attendee';
  if (pathname.startsWith('/exhibitor/')) return 'exhibitor';
  return 'general';
}

function getScreenLabel(screen: string): string {
  const labels: Record<string, string> = {
    home: 'Home',
    agenda: 'Agenda',
    library: 'My Library',
    explore: 'Explore',
    session: 'Session',
    paper: 'Paper',
    speaker: 'Speaker',
    attendee: 'Attendee',
    exhibitor: 'Exhibitor',
    general: 'Conference',
  };
  return labels[screen] || 'Conference';
}

// ─── Agentic quick actions ────────────────────────────────────────────────────

function getAgenticActions(screen: string): { label: string; icon: string; actionType: AgentActionType; metadata: Record<string, string> }[] {
  switch (screen) {
    case 'session':
      return [
        { label: 'Plan review time', icon: 'Clock', actionType: 'block-time', metadata: { duration: '30', topic: 'Session review' } },
      ];
    case 'attendee':
      return [
        { label: 'Draft coffee invite', icon: 'Calendar', actionType: 'schedule-meeting', metadata: { topic: 'Coffee chat' } },
        { label: 'Draft follow-up', icon: 'FileText', actionType: 'draft-followup', metadata: { topic: 'conference discussion' } },
      ];
    case 'paper':
      return [
        { label: 'Plan reading time', icon: 'Clock', actionType: 'block-time', metadata: { duration: '45', topic: 'Paper deep-read' } },
      ];
    default:
      return [];
  }
}

// ─── Types ─────────────────────────────────────────────────────────────────────

type MiniMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  chips?: ActionChip[];
  receiptId?: string;
};

// ─── Context builder ───────────────────────────────────────────────────────────

/**
 * Build AI context for the floating copilot modal based on the current route.
 * For paper/session screens we switch to the relevant mode so the system prompt
 * is appropriate even when the modal doesn't have full entity metadata.
 */
function buildModalContext(pathname: string, currentScreen: string, eventId?: string): AIContext {
  const base: AIContext = { mode: 'general', currentScreen, eventId };

  // Extract paper ID from /paper/:id
  const paperMatch = pathname.match(/^\/paper\/([^/]+)/);
  if (paperMatch) {
    return { ...base, mode: 'paper-insight', paperId: paperMatch[1] };
  }

  // Extract session ID from /session/:id
  const sessionMatch = pathname.match(/^\/session\/([^/]+)/);
  if (sessionMatch) {
    return { ...base, mode: 'session-prep', sessionId: sessionMatch[1] };
  }

  if (currentScreen === 'attendee') {
    return { ...base, mode: 'networking' };
  }

  return base;
}

// ─── Main Component ─────────────────────────────────────────────────────────

interface AICopilotModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AICopilotModal({ isOpen, onClose }: AICopilotModalProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    addNote,
    aiPreferences,
    authSession,
    executeAgentAction,
    currentEventId,
    isAuthSessionCurrent,
  } = useApp();
  const { isEnabled } = useEventModules();
  const { preview } = useEventData();
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<MiniMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [progressLabel, setProgressLabel] = useState('');
  const [runningAction, setRunningAction] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const keyboardVisible = useKeyboardVisible(isOpen);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const threadIdRef = useRef<string | null>(null);
  const threadSummaryRef = useRef<string | null>(null);
  const hasLocalDraftRef = useRef(false);

  const currentScreen = detectScreen(location.pathname);
  const screenLabel = getScreenLabel(currentScreen);
  const suggestions = preview || aiPreferences.aiSuggestions
    ? getContextSuggestions(currentScreen, isEnabled)
    : [];
  const agenticActions = !preview && aiPreferences.aiSuggestions && isEnabled('notes')
    ? getAgenticActions(currentScreen)
    : [];

  // Load existing thread + messages on open
  useEffect(() => {
    const session = authSession;
    let disposed = false;
    const stillCurrent = () =>
      !disposed && (preview || (!!session && isAuthSessionCurrent(session)));

    if (isOpen && (session || preview)) {
      setMessages([]);
      setInput('');
      setIsLoading(false);
      setProgressLabel('');
      setRunningAction(null);
      threadIdRef.current = null;
      threadSummaryRef.current = null;
      hasLocalDraftRef.current = false;
      // Opening the launcher should show suggestions, not summon a keyboard.
      // Move focus to a non-text control for keyboard/screen-reader users.
      closeButtonRef.current?.focus({ preventScroll: true });

      const paperMatch = location.pathname.match(/^\/paper\/([^/]+)/);
      const sessionMatch = location.pathname.match(/^\/session\/([^/]+)/);
      const speakerMatch = location.pathname.match(/^\/speaker\/([^/]+)/);
      const exhibitorMatch = location.pathname.match(/^\/exhibitor\/([^/]+)/);
      const dateLabel = new Date().toLocaleDateString([], { month: 'short', day: 'numeric' });

      let context_type: 'general' | 'paper' | 'session' | 'speaker' | 'exhibitor' = 'general';
      let context_id: string | undefined;
      let title = `General · ${dateLabel}`;
      if (paperMatch) { context_type = 'paper'; context_id = paperMatch[1]; title = 'Paper chat'; }
      else if (sessionMatch) { context_type = 'session'; context_id = sessionMatch[1]; title = 'Session chat'; }
      else if (speakerMatch) { context_type = 'speaker'; context_id = speakerMatch[1]; title = 'Speaker chat'; }
      else if (exhibitorMatch) { context_type = 'exhibitor'; context_id = exhibitorMatch[1]; title = 'Exhibitor chat'; }

      if (!preview && session) void (async () => {
        try {
          if (!stillCurrent()) return;
          const thread = await chatThreadService.findOrCreate(
            { event_id: currentEventId ?? undefined, context_type, context_id, title },
            { signal: session.signal },
          );
          if (!stillCurrent()) return;
          threadIdRef.current = thread.id;
          threadSummaryRef.current = thread.summary ?? null;
          const msgs = await chatThreadService.getMessages(
            thread.id,
            { signal: session.signal },
          );
          if (!stillCurrent()) return;
          if (!hasLocalDraftRef.current) {
            setMessages(msgs.map(m => ({ id: m.id, role: m.role, content: m.content, receiptId: m.receipt_id ?? undefined })));
          }
        } catch {
          /* non-fatal: modal still usable without history */
        }
      })();
    } else {
      setMessages([]);
      setInput('');
      setRunningAction(null);
      threadIdRef.current = null;
      threadSummaryRef.current = null;
      hasLocalDraftRef.current = false;
    }

    return () => {
      disposed = true;
    };
  }, [
    authSession,
    currentEventId,
    isAuthSessionCurrent,
    isOpen,
    location.pathname,
    preview,
  ]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const sendMessage = async (text?: string) => {
    const content = text || input.trim();
    const session = authSession;
    if (
      !content ||
      isLoading ||
      (!preview && (!session || !isAuthSessionCurrent(session)))
    ) return;

    setInput('');
    hasLocalDraftRef.current = true;
    const userMsg: MiniMessage = { id: Date.now().toString(), role: 'user', content };
    setMessages(prev => [...prev, userMsg]);

    if (preview) {
      setMessages(prev => [...prev, {
        id: `${Date.now()}-preview`,
        role: 'assistant',
        content: previewCopilotResponse(currentScreen),
      }]);
      return;
    }

    if (!session) return;
    setIsLoading(true);
    setProgressLabel('Thinking...');

    try {
      const ctx: AIContext = buildModalContext(location.pathname, currentScreen, currentEventId ?? undefined);
      ctx.threadId = threadIdRef.current ?? undefined;
      const history = messages.slice(-20).map(m => ({ role: m.role, content: m.content }));
      const response = await generateAIResponse(
        content,
        ctx,
        (status) => {
          if (isAuthSessionCurrent(session)) setProgressLabel(status);
        },
        undefined,
        history,
        threadSummaryRef.current ?? undefined,
        session.signal,
      );
      if (!isAuthSessionCurrent(session)) return;
      const aiMsg: MiniMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.content,
        chips: response.chips,
        receiptId: response.receiptId,
      };
      setMessages(prev => [...prev, aiMsg]);

      // ── Persist to DB ────────────────────────────────────────────────────
      try {
        if (!threadIdRef.current) {
          if (!isAuthSessionCurrent(session)) return;
          // Fallback if eager load failed on open
          const paperMatch = location.pathname.match(/^\/paper\/([^/]+)/);
          const sessionMatch = location.pathname.match(/^\/session\/([^/]+)/);
          const speakerMatch = location.pathname.match(/^\/speaker\/([^/]+)/);
          const exhibitorMatch = location.pathname.match(/^\/exhibitor\/([^/]+)/);
          const dateLabel = new Date().toLocaleDateString([], { month: 'short', day: 'numeric' });
          let context_type: 'general' | 'paper' | 'session' | 'speaker' | 'exhibitor' = 'general';
          let context_id: string | undefined;
          let title = `General · ${dateLabel}`;
          if (paperMatch) { context_type = 'paper'; context_id = paperMatch[1]; title = 'Paper chat'; }
          else if (sessionMatch) { context_type = 'session'; context_id = sessionMatch[1]; title = 'Session chat'; }
          else if (speakerMatch) { context_type = 'speaker'; context_id = speakerMatch[1]; title = 'Speaker chat'; }
          else if (exhibitorMatch) { context_type = 'exhibitor'; context_id = exhibitorMatch[1]; title = 'Exhibitor chat'; }
          const thread = await chatThreadService.findOrCreate(
            { event_id: currentEventId ?? undefined, context_type, context_id, title },
            { signal: session.signal },
          );
          if (!isAuthSessionCurrent(session)) return;
          threadIdRef.current = thread.id;
          threadSummaryRef.current = thread.summary ?? null;
        }

        if (!isAuthSessionCurrent(session)) return;
        const threadId = threadIdRef.current;
        if (!threadId) return;
        const appendResult = await chatThreadService.appendMessages(
          threadId,
          [
            { role: 'user', content },
            { role: 'assistant', content: response.content, receipt_id: response.receiptId },
          ],
          { signal: session.signal },
        );
        if (!isAuthSessionCurrent(session)) return;
        threadSummaryRef.current = appendResult.summary ?? null;
      } catch {
        // Non-fatal: chat still works, just won't be persisted
      }
    } catch (err) {
      if (
        isAuthSessionCurrent(session) &&
        !(err instanceof DOMException && err.name === 'AbortError')
      ) {
        notify.error('Could not reach AI Copilot', 'Please try again.');
      }
    } finally {
      if (isAuthSessionCurrent(session)) {
        setIsLoading(false);
        setProgressLabel('');
      }
    }
  };

  const handleExpand = () => {
    const ctx = buildModalContext(location.pathname, currentScreen, currentEventId ?? undefined);

    // If AI is mid-response, the last message is an unanswered user question.
    // Strip it from history and pass it as pendingMessage so the full view re-sends it.
    let historyMsgs = messages;
    let pendingMessage: string | undefined;
    if (isLoading) {
      const last = messages[messages.length - 1];
      if (last?.role === 'user') {
        historyMsgs = messages.slice(0, -1);
        pendingMessage = last.content;
      }
    }

    onClose();
    navigate('/report-builder', {
      state: {
        messages: historyMsgs.map(m => ({ role: m.role, content: m.content, receiptId: m.receiptId })),
        context: ctx,
        pendingMessage,
        thread_id: threadIdRef.current ?? undefined,
        thread_summary: threadSummaryRef.current ?? undefined,
      },
    });
  };

  const handleAgenticAction = async (action: typeof agenticActions[0]) => {
    if (!isEnabled('notes')) {
      notify.info('Notes are not available for this event.');
      return;
    }
    const session = authSession;
    if (
      runningAction ||
      !session ||
      !isAuthSessionCurrent(session)
    ) return;
    setRunningAction(action.label);

    // Tie the action to the entity the user is currently viewing so drafts and
    // planning notes reference the real attendee / session / paper.
    const attendeeMatch = location.pathname.match(/^\/attendee\/([^/]+)/);
    const sessionMatch = location.pathname.match(/^\/session\/([^/]+)/);
    const paperMatch = location.pathname.match(/^\/paper\/([^/]+)/);
    const metadata: Record<string, string> = { ...action.metadata };
    if (attendeeMatch) metadata.attendeeId = attendeeMatch[1];
    if (sessionMatch) metadata.sessionId = sessionMatch[1];
    if (paperMatch) metadata.paperId = paperMatch[1];

    const isDraft =
      action.actionType === 'draft-followup' ||
      action.actionType === 'schedule-meeting' ||
      action.actionType === 'send-intro';

    try {
      await executeAgentAction({
        type: action.actionType,
        title: action.label,
        description: getAgentActionDescription(action.actionType, metadata),
        icon: action.actionType,
        metadata,
      }, session);
      if (!isAuthSessionCurrent(session)) return;
      notify.success(
        isDraft ? 'Draft saved to My Notes' : 'Saved to My Notes',
        isDraft ? 'Review and send it from My Notes' : action.label,
      );
    } catch (err) {
      if (isAuthSessionCurrent(session)) {
        notify.error('Could not complete that action', err instanceof Error ? err.message : 'Please try again.');
      }
    } finally {
      if (isAuthSessionCurrent(session)) setRunningAction(null);
    }
  };

  const handleChipAction = (chip: ActionChip) => {
    switch (chip.action) {
      case 'save-note':
      case 'save': {
        if (!isEnabled('notes')) {
          notify.info('Notes are not available for this event.');
          break;
        }
        const lastAI = [...messages].reverse().find(m => m.role === 'assistant');
        const session = authSession;
        if (lastAI && session && isAuthSessionCurrent(session)) {
          const saved = addNote({
            title: `AI Copilot — ${screenLabel}`,
            content: lastAI.content,
            source: currentScreen === 'attendee' ? 'ai-networking' : 'ai-copilot',
            sourceLabel: `AI Copilot on ${screenLabel}`,
          }, session);
          if (saved) notify.success('Saved as note', 'Find it in My Notes');
        }
        break;
      }
      case 'schedule':
        if (!isEnabled('agenda')) break;
        onClose();
        navigate('/agenda');
        break;
      case 'network':
      case 'view-matches':
        if (!isEnabled('attendees')) break;
        onClose();
        navigate('/explore/attendees');
        break;
      case 'follow-up':
        inputRef.current?.focus();
        break;
      default:
        notify.info(chip.label);
    }
  };

  // Truncate long AI messages for mini view
  const truncateContent = (content: string, maxLines = 8): string => {
    const lines = content.split('\n').filter(l => l.trim());
    if (lines.length <= maxLines) return content;
    return lines.slice(0, maxLines).join('\n') + '\n\n*...tap Expand to see full response*';
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40"
            style={{ background: 'rgba(0,0,0,0.6)' }}
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, y: 80 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 80 }}
            transition={{ type: 'spring', damping: 28, stiffness: 350 }}
            className="fixed left-1/2 -translate-x-1/2 z-50 w-[calc(100%-32px)] rounded-2xl border overflow-hidden flex flex-col"
            style={{
              bottom: keyboardVisible ? '0.75rem' : 'calc(var(--app-bottom-nav-height, calc(5rem + env(safe-area-inset-bottom, 0px))) + 1rem)',
              maxWidth: '418px',
              maxHeight: keyboardVisible
                ? 'calc(100dvh - 1.5rem - env(safe-area-inset-top, 0px))'
                : 'min(65dvh, calc(100dvh - var(--app-bottom-nav-height, calc(5rem + env(safe-area-inset-bottom, 0px))) - 2rem - env(safe-area-inset-top, 0px)))',
              background: 'var(--app-surface)',
              borderColor: 'var(--app-border)',
              boxShadow: '0 -4px 40px rgba(0,0,0,0.4), 0 0 80px var(--app-accent-glow)',
            }}
          >
            {/* Header */}
            <div
              className="copilot-header flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b shrink-0"
              style={{ borderColor: 'var(--app-border)' }}
            >
              <div className="min-w-0 flex items-center gap-2.5">
                <div
                  className="size-8 shrink-0 rounded-full flex items-center justify-center"
                  style={{ background: 'var(--app-accent-dim)' }}
                >
                  <Sparkles className="w-4 h-4" style={{ color: 'var(--app-accent)' }} />
                </div>
                <div>
                  <p className="text-sm" style={{ color: 'var(--app-text)' }}>AI Copilot</p>
                  <p className="text-[10px]" style={{ color: 'var(--app-text-muted)' }}>
                    Context: {screenLabel}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {keyboardVisible && <button
                  type="button"
                  onClick={() => inputRef.current?.blur()}
                  aria-label="Hide keyboard"
                  className="size-11 rounded-full flex items-center justify-center"
                  style={{ background: 'var(--app-tag-bg)', color: 'var(--app-accent)' }}
                >
                  <ChevronDown className="size-5" />
                </button>}
                {!preview && !keyboardVisible && <button
                  onClick={() => { onClose(); navigate('/chat-history'); }}
                  className="size-8 rounded-full flex items-center justify-center active:scale-90 transition-transform"
                  style={{ background: 'var(--app-tag-bg)' }}
                  title="Chat history"
                >
                  <History className="w-3.5 h-3.5" style={{ color: 'var(--app-accent)' }} />
                </button>}
                {!preview && <button
                  onClick={handleExpand}
                  className="size-8 rounded-full flex items-center justify-center active:scale-90 transition-transform"
                  style={{ background: 'var(--app-tag-bg)' }}
                  title="Expand to full view"
                >
                  <Maximize2 className="w-3.5 h-3.5" style={{ color: 'var(--app-accent)' }} />
                </button>}
                <button
                  ref={closeButtonRef}
                  onClick={onClose}
                  aria-label="Close AI Copilot"
                  className="size-8 rounded-full flex items-center justify-center active:scale-90 transition-transform"
                  style={{ background: 'var(--app-muted)' }}
                >
                  <X className="w-4 h-4" style={{ color: 'var(--app-text-subtle)' }} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-4 py-3 min-h-0">
              <AIAvailabilityNotice enabled={!preview && currentScreen !== 'attendee'} />
              {messages.length === 0 ? (
                /* Suggestion chips + agentic actions */
                <div className="flex flex-col gap-3">
                  {/* Agentic Actions (Sprint 5) */}
                  {agenticActions.length > 0 && (
                    <>
                      <p className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--app-accent)' }}>
                        Agent Actions
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {agenticActions.map(a => (
                          <button
                            key={a.label}
                            onClick={() => handleAgenticAction(a)}
                            disabled={!!runningAction}
                            className="flex items-center gap-2 px-3 py-2 rounded-xl border text-left active:scale-[0.97] transition-all disabled:opacity-50"
                            style={{
                              background: runningAction === a.label ? 'var(--app-accent-dim)' : 'var(--app-surface-2)',
                              borderColor: runningAction === a.label ? 'var(--app-accent-border)' : 'var(--app-border)',
                            }}
                          >
                            <SuggestionIcon name={a.icon} className="w-3.5 h-3.5" />
                            <span className="text-[11px]" style={{ color: 'var(--app-accent)' }}>
                              {runningAction === a.label ? 'Working...' : a.label}
                            </span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}

                  <p className="text-xs" style={{ color: 'var(--app-text-muted)' }}>
                    Quick actions for {screenLabel}:
                  </p>
                  <div className="flex flex-col gap-2">
                    {suggestions.map(s => (
                      <button
                        key={s.label}
                        onClick={() => sendMessage(s.prompt)}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left active:scale-[0.98] transition-transform"
                        style={{
                          background: 'var(--app-bg)',
                          borderColor: 'var(--app-border)',
                        }}
                      >
                        <div
                          className="size-8 rounded-lg flex items-center justify-center shrink-0"
                          style={{ background: 'var(--app-tag-bg)', color: 'var(--app-accent)' }}
                        >
                          <SuggestionIcon name={s.icon} className="w-4 h-4" />
                        </div>
                        <span className="text-xs" style={{ color: 'var(--app-text-subtle)' }}>
                          {s.label}
                        </span>
                      </button>
                    ))}
                  </div>

                  {/* Conference Recap link */}
                  {!preview && isEnabled('recap') && (
                  <button
                    onClick={() => { onClose(); navigate('/recap'); }}
                    className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs active:scale-[0.98] transition-transform"
                    style={{
                      background: 'linear-gradient(135deg, var(--app-accent-solid), var(--app-accent))',
                      color: '#fff',
                    }}
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    Generate Conference Recap
                  </button>
                  )}

                  {!preview && <button
                    onClick={handleExpand}
                    className="flex items-center justify-center gap-2 py-2.5 rounded-xl border text-xs active:scale-[0.98] transition-transform"
                    style={{
                      background: 'var(--app-tag-bg)',
                      borderColor: 'var(--app-tag-border)',
                      color: 'var(--app-accent)',
                    }}
                  >
                    <Maximize2 className="w-3.5 h-3.5" />
                    Open full AI Copilot
                  </button>}
                </div>
              ) : (
                /* Mini chat messages */
                <div className="flex flex-col gap-3">
                  {messages.map(msg => (
                    <div key={msg.id} className={`flex w-full min-w-0 flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                      <div
                        className={`min-w-0 max-w-[90%] rounded-2xl px-3.5 py-2.5 text-xs leading-relaxed ${
                          msg.role === 'user' ? 'w-fit rounded-tr-sm' : 'rounded-tl-sm border'
                        }`}
                        style={
                          msg.role === 'user'
                            ? { background: 'var(--app-accent-solid)', color: '#fff' }
                            : { background: 'var(--app-bg)', borderColor: 'var(--app-border)', color: 'var(--app-text)' }
                        }
                      >
                        {msg.role === 'assistant' ? (
                          <MarkdownContent content={truncateContent(msg.content)} compact />
                        ) : (
                          <span className="whitespace-pre-wrap" style={{ overflowWrap: 'break-word', wordBreak: 'normal' }}>{msg.content}</span>
                        )}
                      </div>

                      {/* Action chips */}
                      <ReportAIResponseButton receiptId={msg.receiptId} />
                      {msg.role === 'assistant' && filterActionChips(msg.chips, isEnabled).length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          {filterActionChips(msg.chips, isEnabled).slice(0, 3).map(chip => (
                            <button
                              key={chip.action}
                              onClick={() => handleChipAction(chip)}
                              className="text-[10px] px-2.5 py-1 rounded-full border active:scale-95 transition-transform"
                              style={{
                                background: 'var(--app-bg)',
                                borderColor: 'var(--app-accent-border)',
                                color: 'var(--app-accent)',
                              }}
                            >
                              {chip.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Typing indicator */}
                  {isLoading && (
                    <div className="flex items-center gap-2">
                      <div
                        className="rounded-2xl rounded-tl-sm px-3.5 py-2.5 border"
                        style={{ background: 'var(--app-bg)', borderColor: 'var(--app-border)' }}
                      >
                        <div className="flex flex-col gap-1">
                          <div className="flex gap-1 items-center h-3">
                            {[0, 150, 300].map(delay => (
                              <div
                                key={delay}
                                className="w-1 h-1 rounded-full animate-bounce"
                                style={{ background: 'var(--app-accent)', animationDelay: `${delay}ms` }}
                              />
                            ))}
                          </div>
                          {progressLabel && (
                            <p className="text-[10px]" style={{ color: 'var(--app-text-muted)' }}>
                              {progressLabel}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Input */}
            <div className="px-3 py-3 border-t shrink-0" style={{ borderColor: 'var(--app-border)' }}>
              <div
                className="flex items-center gap-2 rounded-xl px-3 border transition-colors focus-within:border-[var(--app-accent)]"
                style={{ background: 'var(--app-bg)', borderColor: 'var(--app-input-border)' }}
              >
                <Sparkles className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--app-text-subtle)' }} />
                <textarea
                  ref={inputRef}
                  rows={2}
                  enterKeyHint="enter"
                  aria-label="Message AI Copilot"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  placeholder="Ask AI anything..."
                  className="min-w-0 flex-1 resize-none overflow-y-auto bg-transparent py-3 text-base leading-6 outline-none"
                  // Small focused inputs trigger iOS WebView viewport zoom.
                  style={{ color: 'var(--app-text)', fontSize: 'max(16px, 1rem)' }}
                />
                <button
                  type="button"
                  onClick={() => sendMessage()}
                  aria-label="Send message"
                  disabled={!input.trim() || isLoading}
                  className="size-11 rounded-lg flex items-center justify-center disabled:opacity-40 active:scale-90 transition-transform shrink-0"
                  style={{ background: 'var(--app-accent-solid)' }}
                >
                  <Send className="w-3.5 h-3.5 text-white" />
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
